import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getAreaCodes } from "../services/area-service";
import subscriptionPlanQueryKeys from "../services/keys";
import { smsSettingsQueryKeys } from "../services/sms-settings-keys";
import { getSmsTemplates, getSmsSettings } from "../services/sms-settings-service";
import { getSubscriptionPlans, getMyActiveSubscription } from "../services/subscription-plan-services";
import { transactionService } from "../services/transactionService";
import type { GetSubscriptionPlansParams } from "../types/subscription-plans";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

export const useGetSubscriptionPlans = (params: GetSubscriptionPlansParams) => {
  return useQuery({
    queryKey: subscriptionPlanQueryKeys.list(params),
    queryFn: () => getSubscriptionPlans(params),
    refetchOnMount: true, // Ensure fresh data on mount
    staleTime: 0, // Always fetch fresh data from server
  });
}; 

export const purchaseHistoryQueryKeys = {
  all: ["purchaseHistory"] as const,
  lists: () => [...purchaseHistoryQueryKeys.all, "list"] as const,
  list: (params: { page: number; offset: number; search?: string; status?: string }) =>
    [...purchaseHistoryQueryKeys.lists(), params] as const,
  stats: () => [...purchaseHistoryQueryKeys.all, "stats"] as const,
};

export const usePurchaseHistory = (params: { page: number; offset: number; search?: string; status?: string }) => {
  return useQuery({
    queryKey: purchaseHistoryQueryKeys.list(params),
    queryFn: async () => {
      const response = await transactionService.getPurchaseHistory(params);

      // Handle response with items array
      if (response?.items) {
        return {
          items: response.items,
          totalPages: response.meta?.totalPages || 1,
          totalItems: response.meta?.totalItems || 0
        };
      }

      // Handle direct array response
      else if (Array.isArray(response)) {
        return {
          items: response,
          totalPages: 1,
          totalItems: response.length
        };
      }

      // Handle null/undefined response - return empty instead of error
      else if (!response || response === null || response === undefined) {

        return {
          items: [],
          totalPages: 1,
          totalItems: 0
        };
      }

      // Log unexpected structure and return empty
      else {

        return {
          items: [],
          totalPages: 1,
          totalItems: 0
        };
      }
    },
    staleTime: 0, // Real-time: always fetch fresh data (important after Stripe redirects)
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for background
    refetchOnMount: true, // Refetch on mount
    refetchOnWindowFocus: true, // Refetch when window regains focus
    retry: 1, // Reduce retries to avoid long spinning
    retryDelay: 1000, // 1 second delay
  });
};

export const usePurchaseStats = () => {
  return useQuery({
    queryKey: purchaseHistoryQueryKeys.stats(),
    queryFn: () => transactionService.getPurchaseHistoryStats(),
    staleTime: 0, // Real-time: always fetch fresh data (important after Stripe redirects)
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for background
    refetchOnMount: true, // Refetch on mount
    refetchOnWindowFocus: true, // Refetch when window regains focus
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useRefreshPurchaseHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: purchaseHistoryQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: purchaseHistoryQueryKeys.stats() }),
      ]);
    },
  });
};

export const useGetAreaCodes = () => {
  return useQuery({
    queryKey: ["areaCodes"],
    queryFn: getAreaCodes,
    refetchOnMount: true, // Ensure fresh data on mount
  });
};

// Hook to get user's active subscription
export const useGetMyActiveSubscription = () => {
  return useQuery({
    queryKey: subscriptionPlanQueryKeys.myActiveSubscription(),
    queryFn: () => getMyActiveSubscription(),
    refetchOnMount: true, // Ensure fresh data on mount
  });
}; 

// Hook to get SMS templates
export const useGetSmsTemplates = () => {
  return useQuery({
    queryKey: smsSettingsQueryKeys.templates(),
    queryFn: getSmsTemplates,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true, // Ensure fresh data on mount
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get SMS settings
export const useGetSmsSettings = () => {
  return useQuery({
    queryKey: smsSettingsQueryKeys.settings(),
    queryFn: getSmsSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true, // Ensure fresh data on mount
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to refresh SMS templates
export const useRefreshSmsTemplates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: smsSettingsQueryKeys.templates() });
    },
  });
};

// Hook to refresh SMS settings
export const useRefreshSmsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: smsSettingsQueryKeys.settings() });
    },
  });
};

// Hook to sync subscription from Stripe and refresh data
export const useSyncSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Call the backend to sync subscription data from Stripe
      await api.post({
        url: getApiUrl(endpoint.subscriptionSync.syncMySubscription),
        auth: true,
      });
    },
    onSuccess: () => {
      // Invalidate all related queries to force refetch
      queryClient.invalidateQueries({ queryKey: subscriptionPlanQueryKeys.myActiveSubscription() });
      queryClient.invalidateQueries({ queryKey: purchaseHistoryQueryKeys.stats() });
      queryClient.invalidateQueries({ queryKey: purchaseHistoryQueryKeys.lists() });
    },
  });
}; 